'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import {
  Box,
  HStack,
  Text,
  VStack,
  Drawer,
  Portal,
  Button,
  Icon,
} from '@chakra-ui/react';
import { FiX, FiChevronDown } from 'react-icons/fi';
import { StoriesModalSkeleton } from '@/components/trader/StoriesModalSkeleton';
import { useInfiniteStoryList } from '@/lib/hooks/useNewStoryData';
import { formatStoryTimeRange } from '@/lib/utils/day.utils';
import {
  formatPercentageDisplay,
  formatPnlColor,
  formatPnLDisplay,
} from '@/lib/utils/format';
import { CryptoIcon } from '@/components/ui/CryptoIcon';
import { TimelineIcon } from '@/components/ui/svgs/TimelineIcon';
import { ModalCloseIcon } from '@/components/ui/svgs/modal-close';
import { useMaxScreenWidthInClient } from '@/lib/hooks/useMaxScreenWidthInClient';

interface StoriesModalProps {
  visible: boolean;
  onClose: () => void;
  address: string;
  symbol: string;
}

export function StoriesModal({
  visible,
  onClose,
  address,
  symbol,
}: StoriesModalProps) {
  const t = useTranslations();
  const router = useRouter();
  const { maxW } = useMaxScreenWidthInClient();
  const [sortBy, setSortBy] = useState<'pnl' | 'time'>('pnl');

  // Fetch story data
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteStoryList(address, symbol, {
    params: { sortBy, sortOrder: 'desc' },
  });

  // Flatten all pages into single array
  const allStories = useMemo(() => {
    return data?.pages.flatMap((page) => page.list) ?? [];
  }, [data]);

  const [winRate, totalPnl] = useMemo(() => {
    return [data?.pages[0]?.winRate ?? 0, data?.pages[0]?.totalPnl ?? 0];
  }, [data]);

  // Load more stories when reaching end
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSortChange = useCallback((newSortBy: 'pnl' | 'time') => {
    setSortBy(newSortBy);
  }, []);

  const renderStoryItem = useCallback(
    (story: any, index: number) => {
      const pnlColor = formatPnlColor(story.pnl === 0, story.pnl > 0);
      const sideColor = story.side.toLowerCase() === 'long' ? 'profit' : 'loss';
      const sideBgColor =
        story.side.toLowerCase() === 'long' ? 'profit/15' : 'loss/15';

      return (
        <Box
          key={`${story.id}-${index}`}
          gap={3}
          rounded="2xl"
          bg="rgba(255, 255, 255, 0.03)"
          p={4}
          cursor="pointer"
          _hover={{
            bg: 'white/10',
            transform: 'translateY(-1px)',
            transition: 'all 0.2s',
          }}
          onClick={() => {
            router.push(`/trader/${address}/story/${story.id}`);
            onClose();
          }}
        >
          {/* Story Content */}
          <VStack gap={3} align="stretch">
            {/* Top Row: Side/Leverage + PnL */}
            <HStack justify="space-between" align="center">
              {/* Side and Leverage Tags */}
              <HStack gap={2.5} align="center">
                <HStack gap={1} align="center">
                  {/* Side Tag */}
                  <Box
                    bg={sideBgColor}
                    rounded="md"
                    px={1.5}
                    py={0.5}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text color={sideColor} fontSize="xs" fontWeight="medium">
                      {story.side.toLowerCase() === 'long'
                        ? t('common.long')
                        : t('common.short')}
                    </Text>
                  </Box>

                  {/* Leverage Tag */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    rounded="md"
                    bg="white/10"
                    px={1.5}
                    py={0.5}
                  >
                    <Text fontSize="xs" color="white/80" fontWeight="medium">
                      {story.leverage}x
                    </Text>
                  </Box>
                </HStack>
              </HStack>

              {/* PnL Display */}
              <HStack gap={1} align="center">
                <Text color={pnlColor} fontSize="sm" fontWeight="semibold">
                  {formatPnLDisplay(story.pnl)}
                </Text>
                <Text color={pnlColor} fontSize="xs" fontWeight="medium">
                  {formatPercentageDisplay(story.pnlPercentage)}
                </Text>
              </HStack>
            </HStack>

            {/* Timeline */}
            <HStack gap={2} align="center">
              {/* Timeline Icon */}
              <Box
                h="3.5"
                w="3.5"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <TimelineIcon size={14} />
              </Box>

              <Text fontSize="xs" color="white/40" fontWeight="medium">
                {formatStoryTimeRange(story.openedAt, story.closedAt)}
              </Text>
            </HStack>
          </VStack>

          {/* Story Title/Analysis */}
          <Text fontSize="sm" color="white/90" lineHeight="1.25rem" mt={3}>
            {story.title || story.analysis}
          </Text>
        </Box>
      );
    },
    [address, onClose, t, router],
  );

  return (
    <Drawer.Root
      open={visible}
      size="xl"
      placement="bottom"
      onOpenChange={(e) => !e.open && onClose()}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner padding={{ base: 0, md: 10 }}>
          <Drawer.Content bg="#171A1C" maxW={maxW} m="0 auto" maxH="80vh">
            <Drawer.Header>
              <HStack justify="space-between" align="center" p={4}>
                <Text fontSize="base" color="white/90" fontWeight="semibold">
                  {symbol} {t('trader.tradingStories')}
                </Text>

                <Button
                  variant="ghost"
                  size="sm"
                  p={1}
                  minW="auto"
                  h="auto"
                  onClick={onClose}
                  _hover={{ bg: 'white/10' }}
                >
                  <ModalCloseIcon />
                </Button>
              </HStack>
            </Drawer.Header>

            <Drawer.Body overflowY="auto" pb={4} scrollbar="hidden">
              <VStack gap={0} align="stretch">
                {/* Win Rate and Total PnL Stats */}
                <Box mx={4} mb={4} mt={4}>
                  <Box
                    border="1px solid"
                    borderColor="rgba(255, 255, 255, 0.10)"
                    rounded="2xl"
                    p={4}
                  >
                    <HStack justify="space-between" align="center">
                      {/* Win Rate */}
                      <VStack flex={1} gap={2} align="flex-start">
                        <Text
                          fontSize="xs"
                          color="white/60"
                          fontWeight="medium"
                        >
                          {t('trader.stats.winRate')}
                        </Text>
                        <Text
                          fontSize="lg"
                          color={formatPnlColor(winRate === 0, winRate > 0)}
                          fontWeight="semibold"
                        >
                          {formatPercentageDisplay(winRate)}
                        </Text>
                      </VStack>

                      {/* Total PnL */}
                      <VStack flex={1} gap={2} align="flex-end">
                        <Text
                          fontSize="xs"
                          color="white/60"
                          fontWeight="medium"
                        >
                          {t('trader.stats.totalPnl')}
                        </Text>
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          color={formatPnlColor(totalPnl === 0, totalPnl > 0)}
                        >
                          {formatPnLDisplay(totalPnl)}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </Box>

                {/* Story List Header */}
                <VStack gap={4} w="full" px={4}>
                  <HStack justify="space-between" align="center" w="full">
                    <Text fontSize="sm" color="white/90" fontWeight="semibold">
                      {t('trader.stories.storyList')} (
                      {data?.pages[0]?.total || 0})
                    </Text>

                    {/* Sort Buttons */}
                    <HStack gap={1}>
                      <Button
                        size="sm"
                        variant={sortBy === 'pnl' ? 'solid' : 'ghost'}
                        bg={sortBy === 'pnl' ? 'profit/15' : 'white/10'}
                        onClick={() => handleSortChange('pnl')}
                        h={7}
                        px={2}
                        py={1.5}
                      >
                        <HStack gap={1} align="center">
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            color={sortBy === 'pnl' ? 'profit' : 'white/70'}
                          >
                            {t('trader.stories.byPnL')}
                          </Text>
                          {sortBy === 'pnl' && (
                            <Box
                              h="3.5"
                              w="3.5"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Icon size="xs" color="#50D2C1">
                                <FiChevronDown />
                              </Icon>
                            </Box>
                          )}
                        </HStack>
                      </Button>

                      <Button
                        size="sm"
                        variant={sortBy === 'time' ? 'solid' : 'ghost'}
                        bg={sortBy === 'time' ? 'profit/15' : 'white/10'}
                        onClick={() => handleSortChange('time')}
                        h={7}
                        px={2}
                        py={1}
                      >
                        <HStack gap={1} align="center">
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            color={sortBy === 'time' ? 'profit' : 'white/70'}
                          >
                            {t('trader.stories.byTime')}
                          </Text>
                          {sortBy === 'time' && (
                            <Box
                              h="3.5"
                              w="3.5"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Icon size="xs" color="#50D2C1">
                                <FiChevronDown />
                              </Icon>
                            </Box>
                          )}
                        </HStack>
                      </Button>
                    </HStack>
                  </HStack>

                  {/* Story Items */}
                  <VStack gap={4} align="stretch" w="full">
                    {isLoading && <StoriesModalSkeleton />}
                    {!isLoading && error && (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={8}
                      >
                        <Text color="white/60">{t('common.error')}</Text>
                      </Box>
                    )}
                    {!isLoading && !error && allStories.length === 0 && (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        py={8}
                      >
                        <Text color="white/60">
                          {t('trader.stories.noStories')}
                        </Text>
                      </Box>
                    )}
                    {!isLoading && !error && allStories.length > 0 && (
                      <>
                        {allStories.map((story, index) =>
                          renderStoryItem(story, index),
                        )}

                        {/* Loading More Indicator */}
                        {isFetchingNextPage && (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            py={4}
                          >
                            <Text fontSize="sm" color="white/60">
                              {t('common.loadingMore')}
                            </Text>
                          </Box>
                        )}

                        {/* Load More Button */}
                        {hasNextPage && !isFetchingNextPage && (
                          <Button
                            variant="ghost"
                            onClick={handleLoadMore}
                            mt={4}
                            color="white/70"
                            _hover={{ color: 'white/90' }}
                          >
                            {t('common.loadMore')}
                          </Button>
                        )}
                      </>
                    )}
                  </VStack>
                </VStack>
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
